import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  startupOptionsBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : "rgba(0,0,0,0.02)",
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { refreshWhatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const initialState = {
    name: "",
    connectionType: "baileys",
    greetingMessage: "",
    farewellMessage: "",
    defaultQueueId: "",
    allowGroupMessages: false,
    isDefault: false,
    importFullHistory: false,
    n8nWebhookEnabled: false,
    n8nWebhookUrl: "",
    n8nWebhookToken: "",
    n8nStopOnAccept: false,
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queueOptions, setQueueOptions] = useState([]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`, {
          params: { _: Date.now() },
        });
        setWhatsApp({
          ...initialState,
          ...data,
          connectionType: data?.connectionType || "baileys",
        });

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [open, whatsAppId]);

  useEffect(() => {
    const fetchQueues = async () => {
      if (!open) return;
      try {
        const { data } = await api.get("/queue");
        setQueueOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchQueues();
  }, [open]);

  const handleSaveWhatsApp = async (values) => {
    const normalizedConnectionType =
      String(values.connectionType || "baileys").toLowerCase() === "waba"
        ? "waba"
        : "baileys";

    const whatsappData = {
      name: values.name,
      isDefault: Boolean(values.isDefault),
      allowGroupMessages: Boolean(values.allowGroupMessages),
      importFullHistory: Boolean(values.importFullHistory),
      n8nWebhookEnabled: Boolean(values.n8nWebhookEnabled),
      n8nWebhookUrl: values.n8nWebhookUrl || "",
      n8nWebhookToken: values.n8nWebhookToken || "",
      n8nStopOnAccept: Boolean(values.n8nStopOnAccept),
      greetingMessage: values.greetingMessage || "",
      farewellMessage: values.farewellMessage || "",
      defaultQueueId: values.defaultQueueId || null,
      queueIds: selectedQueueIds,
    };

    if (!whatsAppId) {
      whatsappData.connectionType = normalizedConnectionType;
    }

    try {
      if (whatsAppId) {
        const { data } = await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        setWhatsApp(data);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      await refreshWhatsApps();
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
  };

  const showBootstrapOptions = !whatsAppId && user?.profile === "admin";

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="isDefault"
                        checked={Boolean(values.isDefault)}
                        onChange={(event) =>
                          setFieldValue("isDefault", event.target.checked)
                        }
                      />
                    }
                    label={i18n.t("whatsappModal.form.default")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="allowGroupMessages"
                        checked={Boolean(values.allowGroupMessages)}
                        onChange={(event) =>
                          setFieldValue("allowGroupMessages", event.target.checked)
                        }
                      />
                    }
                    label="Atender grupos"
                  />
                </div>
                {!whatsAppId && (
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Tipo de conexão</InputLabel>
                    <Field
                      as={Select}
                      label="Tipo de conexão"
                      name="connectionType"
                    >
                      <MenuItem value="baileys">Baileys (QR Code)</MenuItem>
                      <MenuItem value="waba">
                        WABA (WhatsApp Business API)
                      </MenuItem>
                    </Field>
                  </FormControl>
                )}
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Fila padrão da conexão</InputLabel>
                  <Field
                    as={Select}
                    label="Fila padrão da conexão"
                    name="defaultQueueId"
                  >
                    <MenuItem value="">
                      <em>Sem fila padrão</em>
                    </MenuItem>
                    {queueOptions
                      .filter((queue) => selectedQueueIds.includes(queue.id))
                      .map((queue) => (
                        <MenuItem key={queue.id} value={queue.id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                  </Field>
                </FormControl>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    rows={5}
                    fullWidth
                    name="greetingMessage"
                    error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                    helperText={
                      (touched.greetingMessage && errors.greetingMessage) ||
                      "Variáveis: {{name}} e {{protocol}}"
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.farewellMessage")}
                    type="farewellMessage"
                    multiline
                    rows={5}
                    fullWidth
                    name="farewellMessage"
                    error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                    helperText={
                      (touched.farewellMessage && errors.farewellMessage) ||
                      "Variáveis: {{name}} e {{protocol}}"
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
                />
                <Box className={classes.startupOptionsBox}>
                  <Typography variant="subtitle2">
                    Integração n8n
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="n8nWebhookEnabled"
                        checked={Boolean(values.n8nWebhookEnabled)}
                        onChange={(event) =>
                          setFieldValue("n8nWebhookEnabled", event.target.checked)
                        }
                      />
                    }
                    label="Ativar webhook ao receber mensagem"
                  />
                  <Field
                    as={TextField}
                    label="Endpoint webhook"
                    fullWidth
                    name="n8nWebhookUrl"
                    placeholder="https://seu-n8n/webhook/deskadvisor"
                    variant="outlined"
                    margin="dense"
                  />
                  <Field
                    as={TextField}
                    label="Token (Bearer)"
                    fullWidth
                    name="n8nWebhookToken"
                    variant="outlined"
                    margin="dense"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="n8nStopOnAccept"
                        checked={Boolean(values.n8nStopOnAccept)}
                        onChange={(event) =>
                          setFieldValue("n8nStopOnAccept", event.target.checked)
                        }
                      />
                    }
                    label="Interromper n8n quando o chamado for aceito"
                  />
                </Box>
                {showBootstrapOptions && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">
                      Configurações da primeira sincronização
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          name="importFullHistory"
                          checked={Boolean(values.importFullHistory)}
                          onChange={(event) =>
                            setFieldValue("importFullHistory", event.target.checked)
                          }
                        />
                      }
                      label="Puxar histórico dos últimos 10 dias"
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
